import { InputAdornment, TextField } from '@mui/material';
import { StandardTextFieldProps } from '@mui/material/TextField';
import MuiDanger from 'components/MuiExtras/Typography/MuiDanger';
import MuiSuccess from 'components/MuiExtras/Typography/MuiSuccess';
import * as React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';


interface IProps {
    minLength: number;
    maxLength?: number;
    minSpecialChars: number;
    minNumericChars: number;
    minUpperChars: number;
    minLowerChars: number;
    onChange?: (v: string, isValid: boolean) => void;
    getInputRef?: (v: any) => void;
}

type Props = IProps & WithTranslation & Omit<StandardTextFieldProps, "onChange" | "helperText" | "inputRef">;

enum CheckResult { valid, length, numbers, special, lower, upper, unknow }

class MuiPasswordTextField extends React.PureComponent<Props> {

    private refText: any;

    static defaultProps: Partial<Props> = {
        minLength: 6,
        minLowerChars: 0,
        minUpperChars: 0,
        minSpecialChars: 0,
        minNumericChars: 0,
        margin: "normal"
    };

    private changeValue = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const text = e.target.value;
        if (this.props.onChange) this.props.onChange(text, this.isValid() == CheckResult.valid);
    };

    private isValid(): CheckResult {
        if (!this.refText || this.refText.value.length == 0)
            return CheckResult.unknow;

        const value = this.refText.value;
        if (value.length < this.props.minLength)
            return CheckResult.length;

        const numbers = value.match(/[0-9]+/);
        if (this.props.minNumericChars > 0 && (numbers == null || numbers.join("").length < this.props.minNumericChars)) return CheckResult.numbers;

        const specials = value.match(/[#$@]+/);
        if (this.props.minSpecialChars > 0 && (specials == null || specials.join("").length < this.props.minSpecialChars)) return CheckResult.special;

        const lowers = value.match(/[[a-z]+/);
        if (this.props.minLowerChars > 0 && (lowers == null || lowers.join("").length < this.props.minLowerChars)) return CheckResult.lower;

        const uppers = value.match(/[[A-Z]+/);
        if (this.props.minUpperChars > 0 && (uppers == null || uppers.join("").length < this.props.minUpperChars)) return CheckResult.upper;

        return CheckResult.valid;
    }

    private getMessage() {
        switch (this.isValid()) {
            case CheckResult.numbers:
                return this.props.t("Components.MuiPasswordTextField.MinNumbers", { count: this.props.minNumericChars });
            case CheckResult.length:
                return this.props.t("Components.MuiPasswordTextField.MinLength", { count: this.props.minLength });
            case CheckResult.special:
                return this.props.t("Components.MuiPasswordTextField.MinSpecial", { count: this.props.minSpecialChars });
            case CheckResult.lower:
                return this.props.t("Components.MuiPasswordTextField.MinLowerCase", { count: this.props.minLowerChars });
            case CheckResult.upper:
                return this.props.t("Components.MuiPasswordTextField.MinUpperCase", { count: this.props.minUpperChars });
        }
        return null;
    }

    render() {
        const { onChange, maxLength, minLength, minLowerChars, minNumericChars, minSpecialChars, minUpperChars, inputProps, value, ...rest } = this.props;
        const isValid = this.isValid();
        const icon = isValid == CheckResult.unknow
            ? null
            : isValid == CheckResult.valid
                ? <MuiSuccess showIcon />
                : <MuiDanger showIcon />;

        const message = this.getMessage();

        return (
            <TextField
                {...rest}
                helperText={message ? <MuiDanger>{message}</MuiDanger> : null}
                InputProps={
                    {
                        type: "password",
                        endAdornment: icon
                            ? <InputAdornment position='end'>{icon}</InputAdornment >
                            : null
                    }
                }
                inputProps={
                    {
                        ...inputProps,
                        ...{ maxLength: maxLength }
                    }
                }
                inputRef={
                    (ref) => {
                        this.refText = ref; if (this.props.getInputRef) this.props.getInputRef(ref);
                    }
                }
                onChange={this.changeValue}
                value={value}
            />
        );
    }
}

export default withTranslation()(MuiPasswordTextField);